import React, { useEffect, useRef } from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import { animated } from 'react-spring';
import styled, { css } from 'styled-components';

import { useScrollVisibility } from '~/acquisition/utils/useNavOffsetHeight';
import PostregCountdown from '~/shared/components/PostregCountdown/PostregCountdown';
import TypedText from '~/shared/components/TypedText';
import { Link } from '~/techstyle-shared/next-routes';
import {
  Carousel,
  useBreakpoint,
  desktop,
  HeaderAttachment,
  mobile,
  Button,
} from '~/techstyle-shared/react-components';

import useGridInputs from '../hooks/useGridInputs';

const PostRegHeader = styled.header`
  z-index: 3;
  isolation: isolate;
  width: 100%;
`;

const CarouselWrapper = styled.div`
  margin-bottom: 12px;
  ${({ theme: { tabsNode } }) => tabsNode.carousel};
`;

const bubbleTextCellStyle = css`
  width: 152px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const selectorButtonCellStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: max-content;
  margin: 0px 4px;
`;

const TabWrapper = styled.a.attrs({ variant: 'unstyled' })`
  background: transparent;
  padding: 14px 8px;
  border: none;
  height: 100%;
  min-height: 200px;
`;

const PostregWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  font-size: 20px;
  font-weight: 600;
  padding: 0 16px;
  text-align: center;

  ${({ theme }) => theme.tabsNode.countdownWrapper};

  ${desktop`
    margin-top: 16px;
  `}
`;

const BubbleContainerWrapper = styled.div`
  width: 154px;
  background: transparent;
  padding: 14px 10px;
  border: none;
  height: 100%;

  &:hover .bubble-image-wrapper {
    border: 2px solid
      ${({ theme: { colors } }) => colors.builderBubbleImageWithLabelBorder};
  }

  /* 
  * The styles below demonstrate how to style this component when it is selected. By default, the selection styles are not applied.
  * This component is primarily designed for use in Builder.io via the BuilderTab. If you are using it outside of builder.io, 
  *   it is considered a standalone component, and the selection feature is not applicable.
  * These styles serve to showcase a possible "selected" state that can be applied using Builder.io custom styles when used 
  * in conjunction with the BuilderTabs component in builder.io. 
  * */
  &.selected .bubble-image-wrapper {
    border: 2px solid ${({ theme: { colors } }) => colors.palette.black};
  }
  &.selected .bubble-text-label {
    text-decoration: underline;
    text-underline-offset: 2px;
    color: ${({ theme: { colors } }) => colors.palette.black};
  }
`;

const BubbleImageWrapper = styled.div`
  height: 100%;
  min-height: 122px;
  object-fit: cover;
  overflow: hidden;
  border-radius: 100%;
  margin-bottom: 8px;
  border: 2px solid transparent;
  box-shadow: rgba(0, 0, 0, 0.6) 0px 0px 16px -4px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BubbleImage = styled.img`
  border-radius: 100%;
  height: 122px;
`;

const BubbleTextLabel = styled.div`
  ${({ theme: { typography } }) => typography.values.heading5small};
  font-weight: bold;

  white-space: pre-wrap;
  max-width: 132px;
  display: flex;
  align-items: center;
  justify-content: center;
  word-wrap: break-word;
`;

const StyledWrapper = styled.div`
  ${desktop`
    width: 99%;
    height: 100%;
    flex: 2;
  `}
`;

const selectorListWrapperStyle = css`
  // add more radius to outer element to match inner buttons
  border-radius: ${({ theme }) => `calc(${theme.borderRadius} + 4px)`};

  ${mobile`
    border-radius: 0;
    padding: 16px 8px;
  `}

  ${({ theme }) => theme.tabsNode.scrolledTabsSelectorWrapper};
`;

const displayTextSelectorListWrapperStyle = css`
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  justify-content: center;
`;

const StyledCarousel = styled(Carousel)`
  ${selectorListWrapperStyle}
`;

const BasicWrapper = styled.div`
  ${({ $displayText }) =>
    $displayText
      ? displayTextSelectorListWrapperStyle
      : selectorListWrapperStyle}
`;

const SelectButton = animated(styled(Button).attrs({
  variant: 'unstyled',
})`
  padding: ${({ theme }) => `${theme.sizes.xxs}px ${theme.sizes.sm}px`};
  border: 1px solid ${({ theme }) => theme.colors.tabBorder};
  background: ${({ theme }) => theme.colors.tabDeselected};
  transition: background 0.12s 0.08s ease-in-out;

  ${mobile`
    margin: 0;
  `}

  & > span h1 {
    color: ${({ theme: { colors } }) => colors.tabDeselectedText};
  }
  & > span:before {
    content: '';
    user-select: none;
    pointer-events: none;
    ${({ $tabId }) => ($tabId === '1' ? `display: none;` : `display: block;`)}
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: ${({ theme: { sizes } }) => sizes.xxs}px;
    background: transparent;
    opacity: 1;
    transform: translateX(
      ${({ $isSelected, $tab }) => {
        if (!$isSelected && $tab === '1') {
          return '100%';
        } else if (!$isSelected && $tab === '2') {
          return '-100%';
        } else {
          return '0';
        }
      }}
    );
    transition:
      background 0.16s ease-in-out,
      opacity 0.16s ease-in-out,
      padding 0.16s ease-in-out,
      transform 0.16s ease-in-out;
  }

  ${({ theme }) => theme.tabsNode.scrolledTabsSelectors};

  ${({ $isSelected, $displayText, theme }) => {
    if ($isSelected) {
      if ($displayText) {
        return theme.displayTextTabSelected;
      }
      return css`
        background: ${({ theme }) => theme.colors.tabSelected};
        & > span h1 {
          color: ${({ theme }) => theme.colors.tabSelectedText};
        }
        & > span:before {
          opacity: 0;
          transform: translateX(0);
        }
      `;
    } else if ($displayText) {
      return theme.displayTextTab;
    }
  }}
`);

const SelectButtonText = styled(TypedText)`
  font-family: ${({ theme: { fontStacks } }) => fontStacks.baseFamily};
  ${({ theme: { tabsNode } }) => tabsNode.buttonText}
`;

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${({ theme }) => theme.colors.stickyBannerBackground};
  // add more radius to match inner container
  border-radius: ${({ theme }) => `calc(${theme.borderRadius} + 8px)`};
  position: relative;

  &:before {
    content: '';
    z-index: -1;
    position: absolute;
    top: 0;
    display: block;
    background: transparent;
    width: 100%;
    height: 100%;
    transform: rotateY(90deg);
    transition:
      background 0.16s ease-in-out,
      transform 0.16s ease-in-out;
    background: ${({ theme }) => theme.colors.stickyBannerBackground};
    border-top: 1px solid ${({ theme }) => theme.colors.metaNavBorder};
  }

  ${({ $isMounted, $isInHeader }) =>
    $isMounted &&
    $isInHeader &&
    css`
      &:before {
        transform: rotateY(0);
      }
    `}

  ${({ theme }) => theme.tabsNode.scrolledTabsWrapper};
  z-index: 1;
`;

/**
 * Styles are adapted from BuilderBubbleImageWithLabel and BuilderTabs components.
 * This will be updated in the future grid redesign.
 */
const GridSpotlights = () => {
  // const { headerWrapperRef } = useNavElements();
  const { isMobile } = useBreakpoint();
  const bannerRef = useRef();
  const carouselRef = useRef();
  const { asPath } = useRouter();
  const flickityRef = useRef();
  const postRegHeaderRef = useRef();
  const isScrollingTabsVisible = useScrollVisibility(postRegHeaderRef);
  const { gridSpotlights } = useGridInputs();
  const { events } = useRouter();

  const { tabList, hideScrollingLabels, showPostregTimer, postregTimerText } =
    gridSpotlights || {};
  const tabs = tabList?.[0]?.spotlightList;

  const isActiveTab = (link) => {
    const asPathWithoutQuery = asPath?.split('?')[0];
    return asPathWithoutQuery === link;
  };

  // Setup for scrolling selector buttons
  const isDisplayText = false;
  const scrolledDesktopBannerUsesCarousel = config.get(
    'public.brand.tabsNode.scrolledDesktopBannerUsesCarousel'
  );
  const useCarousel =
    (!isDisplayText && isMobile) || scrolledDesktopBannerUsesCarousel;
  const Wrapper = useCarousel ? StyledWrapper : React.Fragment;
  const SelectorListType = useCarousel ? StyledCarousel : BasicWrapper;
  const carouselProps = useCarousel
    ? {
        flickityRef,
        pageDots: false,
        contain: true,
        prevNextButtons: false,
        autoPlay: false,
        percentPosition: false,
        wrapAround: false,
        cellStyle: selectorButtonCellStyle,
      }
    : {};

  const handleTabClick = () => {
    if (window) {
      // scroll to top of grid on tab click after route change
      const handleRouteChange = () => {
        setTimeout(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }, 200);
        events.off('routeChangeComplete', handleRouteChange);
      };
      events.on('routeChangeComplete', handleRouteChange);
    }
  };

  useEffect(() => {
    if (!flickityRef.current) {
      return;
    }
    flickityRef.current.resize();
  }, [isMobile]);

  if (!tabs?.length) {
    return null;
  }

  return (
    <PostRegHeader ref={postRegHeaderRef}>
      <CarouselWrapper
        className="builder-tab-carousel-wrapper"
        ref={carouselRef}
      >
        <Carousel
          flickityRef={flickityRef}
          pageDots={false}
          contain
          imagesLoaded
          resize
          adaptiveHeight
          prevNextButtons={false}
          autoPlay={false}
          wrapAround={false}
          cellStyle={bubbleTextCellStyle}
          draggable={isMobile}
          freeScroll={isMobile}
          removeScrollOnDrag={!isMobile}
          selectedAttraction={isMobile ? 0.025 : 0.01}
          friction={isMobile ? 0.28 : 0.21}
        >
          {tabs?.map(({ title, link, asset }, index) => (
            <Link href={link} key={`${title}-${index}`} passHref>
              <TabWrapper>
                <BubbleContainerWrapper
                  className={isActiveTab(link) ? 'selected' : ''}
                >
                  <BubbleImageWrapper className="bubble-image-wrapper">
                    <BubbleImage src={asset} alt={title} />
                  </BubbleImageWrapper>
                  <BubbleTextLabel>{title}</BubbleTextLabel>
                </BubbleContainerWrapper>
              </TabWrapper>
            </Link>
          ))}
        </Carousel>
      </CarouselWrapper>

      {/* Include the postreg timer in the builder tabs. Note: The scrolling label is baked into the TextTabs component that renders the tabs label */}
      {showPostregTimer && (
        <PostregWrapper>
          <PostregCountdown timerText={postregTimerText} />
        </PostregWrapper>
      )}

      {!hideScrollingLabels && isScrollingTabsVisible && (
        <HeaderAttachment>
          <OuterWrapper
            ref={bannerRef}
            $isInHeader={isScrollingTabsVisible}
            $isMounted={!!bannerRef.current}
          >
            <Wrapper>
              <SelectorListType
                {...carouselProps}
                $displayText={isDisplayText}
                data-autotag="grid-toggle-nav"
              >
                {tabs.map(({ title, link }, index) => {
                  return (
                    <Link
                      key={`${title}-selector-${index}`}
                      href={link}
                      passHref
                    >
                      <a>
                        <SelectButton
                          $isSelected={isActiveTab(link)}
                          onClick={handleTabClick}
                        >
                          <SelectButtonText type="header" size="14med">
                            {title}
                          </SelectButtonText>
                        </SelectButton>
                      </a>
                    </Link>
                  );
                })}
              </SelectorListType>
            </Wrapper>
          </OuterWrapper>
        </HeaderAttachment>
      )}
    </PostRegHeader>
  );
};

export default GridSpotlights;
